import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/context/ProfileContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// Feature flag for new application
const isNewApplicationEnabled = import.meta.env.VITE_FEATURE_NEW_APPLICATION === "true";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Доброго ранку";
  if (hour >= 12 && hour < 18) return "Доброго дня";
  if (hour >= 18 && hour < 22) return "Доброго вечора";
  return "Доброї ночі";
}

export function Dashboard() {
  const { user } = useAuth();
  const { hasStoredProfile } = useProfile();
  const greeting = getGreeting();

  return (
    <AppLayout userRole="applicant">
      <div className="space-y-5">
        {/* Greeting */}
        <div>
          <p className="text-[13px] text-muted-foreground">{greeting}</p>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5">
            {user?.givenname} {user?.lastname}
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                <FileIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold leading-none">0</p>
                <p className="text-[12px] text-muted-foreground mt-1">Усього заявок</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                <ClockIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold leading-none">0</p>
                <p className="text-[12px] text-muted-foreground mt-1">На розгляді</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold leading-none">0</p>
                <p className="text-[12px] text-muted-foreground mt-1">Схвалено</p>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Setup Banner */}
        {!hasStoredProfile && (
          <Link to="/settings" className="block">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-[3px] border-blue-200 p-5 hover:border-blue-300 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                  <SettingsIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[14px] text-blue-900">Налаштуйте профіль організації</p>
                  <p className="text-[12px] text-blue-600 mt-0.5">
                    Збережіть дані організації один раз — вони автоматично заповнюватимуться у нових заявках
                  </p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-blue-400 shrink-0" />
              </div>
            </div>
          </Link>
        )}

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-3">
          {isNewApplicationEnabled ? (
            <Link to="/application/new" className="block">
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5 hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <PlusIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[14px]">Нова заявка</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">Подати заявку на допомогу</p>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              </div>
            </Link>
          ) : (
            <div className="block cursor-not-allowed">
              <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5 opacity-60">
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <PlusIcon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[14px] text-muted-foreground">Нова заявка</p>
                      <span className="px-1.5 py-0.5 text-[9px] font-medium bg-muted text-muted-foreground rounded">
                        Скоро
                      </span>
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-0.5">Функція тимчасово недоступна</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Link to="/applications" className="block">
            <div className="bg-white rounded-2xl border-[3px] border-border/50 p-5 hover:border-primary/50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <FileIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[14px]">Мої заявки</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">Переглянути всі заявки</p>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              </div>
            </div>
          </Link>
        </div>

        {/* Empty State */}
        <div className="bg-white rounded-2xl border-[3px] border-border/50 py-12 px-6">
          <div className="text-center">
            <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <FileIcon className="h-6 w-6 text-muted-foreground/50" />
            </div>
            <h3 className="font-medium text-[15px]">Заявок поки немає</h3>
            <p className="text-[13px] text-muted-foreground mt-1 mb-6">
              {isNewApplicationEnabled 
                ? "Створіть першу заявку на отримання допомоги"
                : "Функція подання заявок скоро буде доступна"
              }
            </p>
            {isNewApplicationEnabled ? (
              <Button asChild>
                <Link to="/application/new" className="inline-flex flex-row items-center gap-1.5">
                  <PlusIcon className="h-4 w-4" />
                  Створити заявку
                </Link>
              </Button>
            ) : (
              <Button disabled className="opacity-60 cursor-not-allowed">
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Скоро
              </Button>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default Dashboard;
